import { Autocomplete, Box, capitalize, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import Spinner from '../../components/Spinner';
import WeekPicker from '../../components/WeekPicker';
import { useTimetable } from '../../redux/timetableSlice';
import { useSelector } from 'react-redux';
import LessonCard from './LessonCard';
import useIsSmallScreen from '../../hooks/useIsSmallScreen';
import useLocalStorage from 'use-local-storage';

const getDaysInWeek = start => {
    return Array(6).fill().map((_, i) => {
        const currentDate = dayjs(start).locale("ru-ru").add(i, "day").toDate()
        return { date: currentDate, day: capitalize(currentDate.toLocaleDateString("ru-ru", { weekday: "short", }) ?? "") }
    })
}

const TimetablePage = () => {

    const userInfo = useSelector(x => x.user.userInfo)

    const [filters, setFilters] = useLocalStorage(
        "eschool_timetable_filters",
        { mainGroup: null, studyYear: null }
    )

    const isEmployee = React.useMemo(() => userInfo?.role?.name === "учитель", [userInfo])

    const { data, dates, handleSetDates, isLoading } = useTimetable(
        userInfo ? isEmployee ? "employee" : "student" : null,
        userInfo?.role?.id,
        filters.mainGroup,
        filters.studyYear,
    )

    const { startDate, endDate, value: dateValue } = dates

    const days = useMemo(() => getDaysInWeek(startDate), [startDate])

    const isSmallScreen = useIsSmallScreen()

    if (isLoading || !startDate || !endDate) return <Spinner height="100%" />

    return <Box sx={{ height: "100%", width: "100%", p: 2 }}>

        <Box sx={{ width: "100%", display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: { xs: 0, sm: 2 } }}>
            {dateValue && <WeekPicker
                textFieldProps={{ label: "Неделя", sx: { width: { xs: "100%", sm: 125 } } }}
                value={dateValue}
                onChangeDates={dates => {
                    handleSetDates({ startDate: dates.firstDay, endDate: dates.lastDay, value: dates.value })
                }}
            />}
            {isEmployee && <>
                <Autocomplete
                    options={[true, false]}
                    value={filters.mainGroup}
                    onChange={(_, value) => setFilters({ ...filters, mainGroup: value })}
                    getOptionLabel={option => option ? "Основные группы" : "Ознакомительные группы"}
                    fullWidth
                    sx={{ width: { xs: "100%", sm: 200 } }}
                    renderInput={props => <TextField
                        label="Тип групп"
                        {...props}
                    />}
                />
                <Autocomplete
                    options={[1, 2]}
                    value={filters.studyYear}
                    onChange={(_, value) => setFilters({ ...filters, studyYear: value })}
                    getOptionLabel={option => `${option} год обучения`}
                    fullWidth
                    sx={{ width: { xs: "100%", sm: 200 } }}
                    renderInput={props => <TextField
                        label="Год обучения"
                        {...props}
                    />}
                />
            </>}
        </Box>
        <Box sx={{
            mt: 2,
            maxWidth: "100%",
            height: { xs: "calc(100% - 162px)", sm: "calc(100% - 82px)" },
            overflow: "auto",
            display: "flex",
            flexDirection: { md: "row", sm: "row", xs: "column" },
            borderTop: 1,
            borderBottom: { sm: "1px solid #ccc", xs: 0 },
            borderColor: "#ccc"
        }}>

            {days.map((day, dayIndex) => <Box
                key={day.day}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    minWidth: 150,
                    maxHeight: "100%",
                    borderLeft: 1,
                    borderRight: isSmallScreen ? 1 : dayIndex === days.length - 1 ? 1 : 0,
                    borderColor: "#ccc",
                }}
            >
                <Box sx={{ width: "100%", borderBottom: 1, borderColor: "#ccc" }}>
                    <Typography variant="h6" align="center">
                        {day.day} ({day.date.toLocaleDateString().slice(0, 5)})
                    </Typography>
                </Box>

                {data.filter(l => l.date.slice(0, 10) === day.date.toISOString().slice(0, 10))
                    .toSorted((a, b) => new Date(a.date) - new Date(b.date))
                    .map(lesson => <LessonCard key={lesson.id} data={lesson} />)}
            </Box>)}

        </Box>
    </Box>
}

export default TimetablePage